import { useMemo } from "react";
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from "@tanstack/react-table";
import type { EnrichmentOperatorStats, EnrichmentRecoveryStats, EnrichmentTeamStatsResponse, RecoveryMeasure } from "@/api/enrichment";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { statusColors } from "@/lib/constants";
import { RecoveryRing } from "./RecoveryRing";

const MEASURES = [
  ["with_phone", "Phone"], ["with_email", "Email"], ["with_both", "Both"],
  ["with_either", "Phone or email"], ["without_contact", "Neither"],
] as const;

function describeMeasure(measure: RecoveryMeasure): string {
  return `${measure.count.toLocaleString("en-US")} (${measure.percentage === null ? "n/a" : `${measure.percentage.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`})`;
}

interface EnrichmentRecoveryPanelProps {
  data: EnrichmentTeamStatsResponse;
}

export function EnrichmentRecoveryPanel({ data }: EnrichmentRecoveryPanelProps) {
  const columns = useMemo<ColumnDef<EnrichmentOperatorStats>[]>(() => [
    { id: "operator", header: "Operator", cell: ({ row }) => row.original.full_name || row.original.email || "Unknown operator" },
    { id: "processed", header: "Processed", cell: ({ row }) => row.original.recovery.processed.toLocaleString("en-US") },
    ...MEASURES.map(([key, label]) => ({
      id: key, header: label,
      cell: ({ row }: { row: { original: EnrichmentOperatorStats } }) => describeMeasure(row.original.recovery[key]),
    })),
  ], []);
  const table = useReactTable({ data: data.operators, columns, getCoreRowModel: getCoreRowModel() });
  const recovery: EnrichmentRecoveryStats | undefined = data.total.recovery;
  if (!recovery) {
    return <p role="status" className="rounded-plane bg-slate-50 p-5 text-sm text-slate-600">Recovery statistics are not available yet.</p>;
  }
  return (
    <section aria-label="Contact recovery" className="space-y-5 rounded-plane bg-white p-5 shadow-plane sm:p-6">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Contact recovery</h3>
        <p className="mt-1 text-sm text-slate-500">{recovery.processed.toLocaleString("en-US")} processed {recovery.processed === 1 ? "record" : "records"}. Every percentage uses all processed records.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <RecoveryRing label="Phone" measure={recovery.with_phone} tone={statusColors.qualified.text} />
        <RecoveryRing label="Email" measure={recovery.with_email} tone={statusColors.signed.text} />
        <RecoveryRing label="Both" measure={recovery.with_both} tone={statusColors.responded.text} />
        <RecoveryRing label="Neither" measure={recovery.without_contact} tone={statusColors.default.text} />
      </div>
      <p className="text-xs leading-relaxed text-slate-500">
        Historical reference, 19,002 legacy records: 25.4% with a phone, 9.4% email only, 65.2% neither.
      </p>
      <p className="text-xs text-slate-500">Based on contact details present after processing. Phone and email overlap; paid fields do not determine these rates.</p>
      {recovery.unknown.count > 0 && <p role="note" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
        {recovery.unknown.count} records have no reliable contact state for the time of processing. They remain in the denominator and are not counted as “Neither”.
      </p>}
      {recovery.processed === 0 && <p className="text-sm text-slate-500">No records were processed in this period. Rates are unavailable.</p>}
      <Table className="min-w-[780px] text-sm" aria-label="Recovery by operator">
        <TableHeader>{table.getHeaderGroups().map(group => <TableRow key={group.id}>
          {group.headers.map(header => <TableHead key={header.id} className={header.index === 0 ? "" : "text-right"}>
            {flexRender(header.column.columnDef.header, header.getContext())}
          </TableHead>)}
        </TableRow>)}</TableHeader>
        <TableBody>{table.getRowModel().rows.map(row => <TableRow key={row.original.user_id}>
          {row.getVisibleCells().map((cell, index) => <TableCell key={cell.id} className={index === 0 ? "font-medium" : "text-right tabular-nums whitespace-nowrap"}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>)}
        </TableRow>)}</TableBody>
        <TableFooter><TableRow>
          <TableCell>{data.scope === "team" && !data.user_id ? "Team total" : "Total"}</TableCell>
          <TableCell className="text-right tabular-nums">{recovery.processed.toLocaleString("en-US")}</TableCell>
          {MEASURES.map(([key]) => <TableCell key={key} className="whitespace-nowrap text-right tabular-nums">{describeMeasure(recovery[key])}</TableCell>)}
        </TableRow></TableFooter>
      </Table>
    </section>
  );
}
